'use client';

import { Content, asLink } from '@prismicio/client';
import { PrismicRichText, SliceZone } from '@prismicio/react';
import { components } from 'slices';
import EventNames from 'src/analytics/EventNames';
import { trackClick, TrackPage } from 'src/analytics/Events';
import { Button } from '@get-mosh/msh-shared';
import Heading from 'src/components/Heading/Heading';
import LayoutPerformancePage from 'src/components/Layout/PerformancePage';
import { Subvertical, Vertical } from 'src/constants/Verticals';
import { ComparisonTable } from 'src/views/ComparisonTable/ComparisonTable';
import classNames from 'classnames';
import { PageType } from 'src/analytics/EventProps';
import { getSEOFaqMarkup } from 'src/helpers/seoUtils';

type ComparisonPageProps = {
    page: Content.ComparisonPageDocument<string>;
};

const ComparisonPage = ({ page }: ComparisonPageProps) => {
    const faqSchema = getSEOFaqMarkup(page.data.faq_questions);
    const defaultBackground = 'bg-sand-light';

    return (
        <>
            {faqSchema ? (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(faqSchema),
                    }}
                />
            ) : null}
            {page.data.title ? (
                <TrackPage
                    name={page.data.title}
                    eventProps={{
                        vertical: page.data.vertical as Vertical,
                        sub_vertical: page.data.subvertical as Subvertical,
                        page_type: PageType.COMPETITOR_PAGE,
                    }}
                />
            ) : null}
            <LayoutPerformancePage
                config={{
                    footer_links: page.data.footer_links,
                    footer_copyright: page.data.footer_copyright ?? '',
                    footer_default: page.data.default_footer,
                    navigation_cta_label: page.data.navigation_cta_label ?? '',
                    navigation_cta_link:
                        asLink(page.data.navigation_cta_link) ?? '',
                    navigation_theme_dark: page.data.navigation_theme_dark,
                    navigation_variant: page.data.navigation_variant,
                    navigation_mobile_cta: page.data.navigation_mobile_cta,
                    navigation_web_cta: page.data.navigation_web_cta,
                    navigation_sticky: page.data.navigation_sticky,
                    topbar: page.data.topbar,
                    topbar_messages: [
                        page.data.message_1,
                        page.data.message_2,
                        page.data.message_3,
                        page.data.message_4,
                        page.data.message_5,
                        page.data.message_6,
                    ].filter((message) => message?.length > 0),
                    topbar_background: page.data.topbar_background,
                }}
                className={defaultBackground}
            >
                <SliceZone
                    slices={page.data.slices}
                    components={components}
                    context={{ uid: page.uid, pageType: page.type }}
                />
                {page.data.slices1.length > 1 && (
                    <section
                        className={classNames(
                            'flex flex-col py-10 md:py-20',
                            defaultBackground,
                        )}
                    >
                        <Heading className="mx-auto pb-10 md:pb-14">
                            {page.data.title}
                        </Heading>
                        <ComparisonTable
                            slices={
                                page.data
                                    .slices1 as unknown as Array<Content.ComparisonTableRowSlice>
                            }
                            uid={page.uid}
                        />
                        <div className="mx-auto mt-10 flex w-full flex-col items-center justify-center gap-3 px-4 md:mt-16 md:gap-6">
                            <Button
                                text={page.data.link_label ?? ''}
                                as="a"
                                href={page.data.link_path ?? ''}
                                onClick={() => {
                                    trackClick(EventNames.BUTTON_CLICKED, {
                                        page_type: page.type,
                                        vertical: page.data
                                            .vertical as Vertical,
                                        label: page.data.link_label ?? '',
                                        link: page.data.link_path ?? '',
                                        variation: 'comparison table',
                                        location: 'comparison table',
                                    });
                                }}
                            />
                            <PrismicRichText
                                field={page.data.text_under_link}
                                components={{
                                    paragraph: ({ children }) => (
                                        <p className="text-center text-xs leading-tight tracking-tight">
                                            {children}
                                        </p>
                                    ),
                                    listItem: ({ children }) => (
                                        <li className="text-center text-xs leading-tight tracking-tight">
                                            {children}
                                        </li>
                                    ),
                                    oListItem: ({ children }) => (
                                        <li className="text-center text-xs leading-tight tracking-tight">
                                            {children}
                                        </li>
                                    ),
                                }}
                            />
                        </div>
                    </section>
                )}
                <SliceZone
                    slices={page.data.slices3}
                    components={components}
                    context={{ uid: page.uid, pageType: page.type }}
                />
            </LayoutPerformancePage>
        </>
    );
};

export default ComparisonPage;
